import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createIntegrationCredential,
  QUERY_INTEGRATION_CREDENTIALS
} from "api/landlord/integrations";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useFormTyped } from "shared/hooks/use-form-typed";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import {
  IntegrationId,
  isPmsIntegration,
  Step
} from "views/settings-integration/components/AddIntegrationModal/types";
import { PropertyManagementSystem } from "shared/types/partner";

import * as yup from "yup";
import { QUERY_CURRENT_PARTNER } from "api/landlord/landlord";

interface NameStepProps {
  selectedIntegration: PropertyManagementSystem | IntegrationId;
  credentials: Record<string, string | boolean>;
  setCurrentStep: (step: Step) => void;
  closeModal: () => void;
}

const validationSchema = yup.object({
  name: yup.string().required("Integration name is required")
});

export const NameStep = ({
  selectedIntegration,
  credentials,
  setCurrentStep,
  closeModal
}: NameStepProps) => {
  const queryClient = useQueryClient();

  const form = useFormTyped<{ name: string }>({ name: "" }, validationSchema);

  const { mutate: create, isPending } = useMutation({
    mutationKey: ["INTEGRATION_CREDENTIAL", "CREATE"],
    mutationFn: () => {
      const { rentvine_use_property_groups, ...rentvineCredentials } =
        credentials;

      return createIntegrationCredential({
        credential_name: form.values.name,
        integration: selectedIntegration,
        ...(selectedIntegration === PropertyManagementSystem.Rentvine
          ? {
              credentials: rentvineCredentials,
              settings: { rentvine_use_property_groups }
            }
          : { credentials })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_INTEGRATION_CREDENTIALS]
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_CURRENT_PARTNER] });
      toast.success("Integration added successfully!");
      closeModal();
    },
    onError: (err) => {
      toast.error("Error adding integration: " + getErrorMessage(err));
    }
  });

  const hasPreviousStep = isPmsIntegration(selectedIntegration);

  const handleSubmit = form.onSubmit({
    onSuccess: () => create()
  });

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <BaseInput
        id="name"
        label="Name"
        placeholder="Integration name"
        {...form.register("name")}
        required
      />

      <div className="flex ml-auto gap-2 mt-2">
        <Button
          type="button"
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          onClick={() =>
            setCurrentStep(hasPreviousStep ? Step.SECOND : Step.FIRST)
          }
          className="w-[125px]"
        >
          Back
        </Button>
        <Button
          type="submit"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
          loading={isPending}
        >
          Create
        </Button>
      </div>
    </form>
  );
};
